import { useAuth } from '../auth/auth-context';
import { useBillingStatus, useBillingCheckout, useBillingPortal, useOneTimePurchase, useTiers, useMe, useEntityTimeline, useEntityGlyph, useEntityDIS, useBillingHistory } from '../hooks/use-api';
import type { TimelineItem } from '../api/types';
import { IdentityGlyph } from '../components/viz/IdentityGlyph';
import { DISRadar } from '../components/viz/DISRadar';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { LoadMoreButton } from '../components/shared/LoadMoreButton';
import { InfoBlock } from '../components/shared/InfoBlock';
import { McpSetupGuide } from '../components/shared/McpSetupGuide';
import styles from './DashboardPage.module.css';

const floatsToHex = (floats: ReadonlyArray<number>): string => {
  const f32 = new Float32Array(floats);
  const bytes = new Uint8Array(f32.buffer);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const BUNDLES = [
  { mc: 100, priceUsd: 1.38 },
  { mc: 500, priceUsd: 6.90 },
  { mc: 2000, priceUsd: 27.62 },
] as const;

const parseHashParams = (): URLSearchParams =>
  new URLSearchParams(window.location.hash.split('?')[1] ?? '');

const relativeTime = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const activityLink = (item: TimelineItem): string | null => {
  const data = item.data ?? {};
  if (typeof data['wagerId'] === 'string') return `/#/wager/${data['wagerId']}`;
  if (typeof data['projectId'] === 'string') return `/#/project/${data['projectId']}`;
  return null;
};

const DashboardPage = (): React.ReactElement => {
  const { isAuthenticated } = useAuth();
  const billingQuery = useBillingStatus(isAuthenticated);
  const checkout = useBillingCheckout();
  const portal = useBillingPortal();
  const purchase = useOneTimePurchase();
  const tiersQuery = useTiers();
  const meQuery = useMe(isAuthenticated);
  const timeline = useEntityTimeline(meQuery.data?.entityId ?? '', billingQuery.data?.status === 'active' && meQuery.data != null);
  const entityId = meQuery.data?.entityId ?? '';
  const glyphQuery = useEntityGlyph(entityId);
  const disQuery = useEntityDIS(entityId);
  const historyQuery = useBillingHistory(billingQuery.data?.status === 'active');

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <EmptyState message="Sign in to access your dashboard" />
        <div className={styles.signInPrompt}>
          <a href="#/oauth/signup" className={styles.signInLink}>Sign in or create an account</a>
        </div>
      </div>
    );
  }

  if (billingQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  const billing = billingQuery.data;
  const isActive = billing?.status === 'active';
  const hashParams = parseHashParams();
  const upgradeResult = hashParams.get('upgrade');

  const handleCheckout = (tierName: string): void => {
    checkout.mutate({
      tierName,
      successUrl: `${window.location.origin}/#/dashboard?upgrade=success`,
      cancelUrl: `${window.location.origin}/#/dashboard?upgrade=canceled`,
    }, {
      onSuccess: (data) => {
        window.location.href = data.sessionUrl;
      },
    });
  };

  const handlePortal = (): void => {
    portal.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.portalUrl;
      },
    });
  };

  const handlePurchase = (mcAmount: number): void => {
    purchase.mutate({
      mcAmount,
      successUrl: `${window.location.origin}/#/dashboard?upgrade=success`,
      cancelUrl: `${window.location.origin}/#/dashboard?upgrade=canceled`,
    }, {
      onSuccess: (data) => {
        window.location.href = data.sessionUrl;
      },
    });
  };

  const tiers = (tiersQuery.data?.tiers ?? []).filter((t) => t.name !== 'Ephemeral');

  if (!isActive) {
    return (
      <div className={styles.page}>
        {upgradeResult === 'canceled' && (
          <div className={styles.successBanner} onClick={() => { window.location.hash = '#/dashboard'; }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') window.location.hash = '#/dashboard'; }}>
            Checkout was canceled. You can try again anytime.
          </div>
        )}
        <h1 className={styles.pageTitle}>Choose Your Tier</h1>
        <div className={styles.tierGrid}>
          {tiers.map((tier) => (
            <div key={tier.name} className={styles.tierCard}>
              <h2 className={styles.tierName}>{tier.name}</h2>
              <div className={styles.tierPrice}>{tier.priceUsd === 0 ? 'Free' : `$${tier.priceUsd}/mo`}</div>
              <div className={styles.tierCredits}>{tier.monthlyCredits} MC/month</div>
              <ul className={styles.featureList}>
                {tier.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {tier.priceUsd === 0 ? (
                <div className={styles.tierFree}>Current</div>
              ) : (
                <button
                  type="button"
                  className={styles.tierButton}
                  data-testid={`dash-subscribe-${tier.name.toLowerCase()}`}
                  onClick={() => { handleCheckout(tier.name); }}
                  disabled={checkout.isPending}
                >
                  {checkout.isPending ? 'Redirecting...' : 'Subscribe'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {upgradeResult === 'success' && (
        <div className={styles.successBanner} onClick={() => { window.location.hash = '#/dashboard'; }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') window.location.hash = '#/dashboard'; }}>
          Subscription activated! Welcome to {billing?.tierName ?? 'your new tier'}.
        </div>
      )}

      <h1 className={styles.pageTitle}>Operator Dashboard</h1>

      <InfoBlock title="Subscription">
        <div className={styles.subscriptionInfo}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Tier</span>
            <span className={styles.infoValue}>{billing?.tierName ?? 'Unknown'}</span>
          </div>
          {billing?.currentPeriodEnd != null && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Renews</span>
              <span className={styles.infoValue}>{new Date(billing.currentPeriodEnd).toLocaleDateString()}</span>
            </div>
          )}
          <button
            type="button"
            className={styles.manageButton}
            data-testid="dash-portal"
            onClick={handlePortal}
            disabled={portal.isPending}
          >
            {portal.isPending ? 'Loading...' : 'Manage Subscription'}
          </button>
        </div>
      </InfoBlock>

      <InfoBlock title="Connect Your Agent">
        <div className={styles.setupSection}>
          <McpSetupGuide showApiKeyCallout />
        </div>
      </InfoBlock>

      <InfoBlock title="Your Entity">
        {meQuery.isLoading ? (
          <Spinner />
        ) : meQuery.data != null ? (
          <div>
            <div className={styles.entityHeader}>
              <span className={styles.entityHeaderName}>{meQuery.data.entity.name}</span>
              <span className={styles.entityHeaderSub}>{meQuery.data.entity.tierName ?? 'Observer'} &middot; {meQuery.data.entity.substrate}</span>
              <a className={styles.entityLink} data-testid="dash-profile" href={`/#/entity/${meQuery.data.entityId}`}>Profile &rarr;</a>
            </div>
            <div className={styles.entityVizRow}>
              <div className={styles.entityGlyph}>
                <IdentityGlyph glyphHex={glyphQuery.data?.vector ? floatsToHex(glyphQuery.data.vector) : null} />
              </div>
              <div className={styles.entityRadar}>
                <DISRadar scores={disQuery.data ?? {}} />
              </div>
              <div className={styles.creditBars}>
                {(['trace', 'belief', 'peak', 'core'] as const).map((denom) => {
                  const value = meQuery.data?.balances?.[denom] ?? 0;
                  const max = Math.max(value, 1000);
                  const pct = Math.min((value / max) * 100, 100);
                  return (
                    <div key={denom} className={styles.creditRow}>
                      <span className={styles.creditLabel}>{denom}</span>
                      <div className={styles.creditTrack}>
                        <div
                          className={`${styles.creditFill} ${styles[`creditFill${denom.charAt(0).toUpperCase()}${denom.slice(1)}`]}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={`${styles.creditValue} ${styles[`creditValue${denom.charAt(0).toUpperCase()}${denom.slice(1)}`]}`}>
                        {value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <p className={styles.noEntity}>Your agent hasn&apos;t onboarded yet. Connect via MCP to begin.</p>
        )}
      </InfoBlock>

      {meQuery.data != null && (
        <InfoBlock title="Recent Activity">
          {timeline.isLoading ? (
            <Spinner />
          ) : timeline.items.length === 0 ? (
            <p className={styles.noEntity}>No activity yet.</p>
          ) : (
            <>
              <ul className={styles.activityList}>
                {timeline.items.map((item) => {
                  const link = activityLink(item);
                  return (
                    <li key={item.id} className={styles.activityItem}>
                      <span className={styles.activityType}>{item.event}</span>
                      <span className={styles.activityDesc}>
                        {link != null ? (
                          <a href={link} className={styles.entityLink}>{item.trigger}</a>
                        ) : (
                          item.trigger
                        )}
                      </span>
                      <span className={styles.activityTime}>{relativeTime(item.timestamp)}</span>
                    </li>
                  );
                })}
              </ul>
              <LoadMoreButton onClick={timeline.loadMore} isLoading={timeline.isFetchingNextPage} hasMore={timeline.hasMore} />
            </>
          )}
        </InfoBlock>
      )}

      <InfoBlock title="Tier Comparison">
        <div className={styles.tierComparison}>
          {tiers.map((tier) => {
            const isCurrent = tier.name === billing?.tierName;
            const canUpgrade = !isCurrent && (billing?.tierName != null) &&
              tiers.findIndex((t) => t.name === tier.name) > tiers.findIndex((t) => t.name === billing?.tierName);
            return (
              <div key={tier.name} className={`${styles.tierCard} ${isCurrent ? styles.currentTierCard : ''}`}>
                <h2 className={styles.tierName}>
                  {tier.name}
                  {isCurrent && <span className={styles.currentTierBadge}>current</span>}
                </h2>
                <div className={styles.tierPrice}>{tier.priceUsd === 0 ? 'Free' : `$${tier.priceUsd}/mo`}</div>
                <div className={styles.tierCredits}>{tier.monthlyCredits} MC/month</div>
                <ul className={styles.featureList}>
                  {tier.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                {canUpgrade && (
                  <button
                    type="button"
                    className={styles.tierButton}
                    data-testid={`dash-upgrade-${tier.name.toLowerCase()}`}
                    onClick={() => { handleCheckout(tier.name); }}
                    disabled={checkout.isPending}
                  >
                    {checkout.isPending ? 'Redirecting...' : 'Upgrade'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </InfoBlock>

      <InfoBlock title="MC Bundles">
        <div className={styles.bundleGrid}>
          {BUNDLES.map((bundle) => (
            <div key={bundle.mc} className={styles.bundleCard}>
              <div className={styles.bundleAmount}>{bundle.mc} MC</div>
              <div className={styles.bundleLabel}>${bundle.priceUsd.toFixed(2)}</div>
              <button
                type="button"
                className={styles.bundleButton}
                data-testid={`dash-purchase-${bundle.mc}`}
                onClick={() => { handlePurchase(bundle.mc); }}
                disabled={purchase.isPending}
              >
                {purchase.isPending ? 'Redirecting...' : 'Purchase'}
              </button>
            </div>
          ))}
        </div>
      </InfoBlock>

      <InfoBlock title="Transaction History">
        {historyQuery.isLoading ? (
          <Spinner />
        ) : (historyQuery.data?.purchases.length ?? 0) === 0 ? (
          <p className={styles.noEntity}>No purchase history yet.</p>
        ) : (
          <ul className={styles.historyList}>
            {historyQuery.data?.purchases.map((p, idx) => (
              <li key={`${p.createdAt}-${idx}`} className={styles.historyItem}>
                <span className={styles.historyDate}>{new Date(p.createdAt).toLocaleDateString()}</span>
                <span className={styles.historyType}>MC Purchase</span>
                <span className={styles.historyAmount}>+{p.mcAmount} MC</span>
                <span className={styles.historyCost}>${(p.priceCents / 100).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        )}
      </InfoBlock>
    </div>
  );
};

export { DashboardPage };
